import { FieldTypes } from 'constants/field-types-enum';
import { textWithReferences } from 'helpers/form';
import addValidators from 'helpers/validators/apply-validators';
import { useLocale } from 'hooks/locale';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormMessage, ValidationAlert } from 'components/FormComponents';
import FadeInMotion from 'components/Motion/FadeInMotion';
import ShakeMotion from 'components/Motion/ShakeMotion';
import StatusPill from 'components/Pills/StatusPills';

const QuestionWrapper = ({
  question,
  questionIndex,
  textParams = {},
  control,
  register,
  setValue,
  trigger,
  getValues,
  locale = 'en_GB',
  error,
  componentMap,
  checkValues,
  getComponentProps,
  keyboardHint,
  animationDisabled = false,
  requirementVariantStyle = 'pill',
  gap = 'large',
  titleSize = 'xx-large',
  fieldName,
  hideHeader = false,
  isVisible = true,
  allValues,
  errors,
  rejectedData,
}) => {
  const { t } = useTranslation();
  const { dateHyphenFormat } = useLocale(locale);

  const QuestionComponent = componentMap[question.type];
  const { controlledInput = true, ...componentProps } = getComponentProps();
  // fallback to question.id if fieldName isn't passed
  const name = fieldName || `${question.id}`;

  // Create comprehensive validation rules
  const validationRules = isVisible
    ? addValidators(
        question.type,
        question.min,
        question.max,
        question.required,
        question.validatorName,
        question.validationParams,
        getValues,
        name,
      )
    : {};

  const noHeaderComponents = ['checkbox', 'consent', 'download'];

  if (question?.type === 'statement') {
    return (
      <div className="d-flex flex-column u-gap-small">
        {question.text && (
          <div
            dangerouslySetInnerHTML={{
              __html: textWithReferences(question.text, textParams),
            }}
          />
        )}
        {question?.helpText && (
          <FormMessage message={question?.helpText} type="Info" noMargin closeable={false} />
        )}
      </div>
    );
  }

  if (question.type === 'object') {
    return null;
  }

  if (!QuestionComponent) {
    return <p>Unsupported question type: {question.type}</p>;
  }

  const validationCopyArray = [
    t('components.form.validation.errorTextOne'),
    t('components.form.validation.errorTextTwo'),
    t('components.form.validation.errorTextThree'),
    t('components.form.validation.errorTextFour'),
    t('components.form.validation.errorTextFive'),
    t('components.form.validation.errorTextSix'),
  ];

  return (
    <div
      className={` flex-column u-gap-${gap} u-width-100 ${question.type === 'hidden' ? 'd-hide' : 'd-flex'}`}>
      {!hideHeader && (
        <>
          {requirementVariantStyle === 'pill' && (
            <span className="secondary-text u-font-size-md u-font-weight-400">
              <ShakeMotion shake={error} disabled={animationDisabled}>
                <StatusPill
                  text={question.required ? 'Required' : 'Optional'}
                  large
                  status={question.required ? 'warning' : 'neutral'}
                  show={questionIndex + 1}
                />
              </ShakeMotion>
            </span>
          )}
          {!noHeaderComponents.includes(question.type) && (
            <div className="d-flex flex-column u-gap-small">
              <div className="d-flex flex-row u-gap-small align-items-center">
                <span className={`primary-text u-font-size-${titleSize} u-font-weight-500`}>
                  {textWithReferences(question?.text || question?.title, textParams)}
                </span>
                {requirementVariantStyle === 'text' && !question.required && (
                  <StatusPill text={'Optional'} status={'light'} />
                )}
              </div>
              {question.description && (
                <span className="secondary-text u-font-size-x-small u-font-weight-400">
                  {textWithReferences(question.description, textParams)}
                </span>
              )}
            </div>
          )}
        </>
      )}
      <div className="d-flex flex-column u-gap-small">
        {controlledInput ? (
          <Controller
            name={name}
            control={control}
            rules={validationRules}
            defaultValue={question?.defaultValue}
            render={({ field: { onChange, value }, fieldState: { error: fieldError } }) => {
              const normalizedValue = (() => {
                if (value !== undefined && value !== null) {
                  return value;
                }
                if (question?.defaultValue !== undefined && question?.defaultValue !== null) {
                  return question.defaultValue;
                }
                if (
                  question?.type === FieldTypes.Checkbox ||
                  question?.type === FieldTypes.Consent
                ) {
                  return false;
                }
                if (
                  question?.type === FieldTypes.MultipleChoice &&
                  question?.options?.multiple_selection
                ) {
                  return [];
                }
                if (question?.type === FieldTypes.MultiSelect) {
                  return [];
                }
                return '';
              })();

              return (
                <QuestionComponent
                  {...getComponentProps(normalizedValue)}
                  question={question}
                  format={dateHyphenFormat}
                  value={normalizedValue}
                  textParams={textParams}
                  checked={normalizedValue}
                  onChange={onChange}
                  error={fieldError?.message || error}
                  setValue={setValue}
                  trigger={trigger}
                  componentMap={componentMap}
                  showKeyboardHelp
                  // Pass react-hook-form props for IdRequirementsWrapper
                  control={control}
                  getValues={getValues}
                  name={name}
                  allValues={allValues}
                  errors={errors}
                  rejectedData={rejectedData}
                />
              );
            }}
          />
        ) : (
          <QuestionComponent
            {...componentProps}
            question={question}
            componentMap={componentMap}
            format={dateHyphenFormat}
            register={register}
            control={control}
            setValue={setValue}
            trigger={trigger}
            name={name}
            validators={validationRules}
            showKeyboardHelp
            getComponentProps={getComponentProps}
            getValues={getValues}
            isVisible={isVisible}
            allValues={allValues}
            errors={errors}
            error={error}
            rejectedData={rejectedData}
          />
        )}
        {keyboardHint}
      </div>
      {question?.helpText && (
        <FormMessage message={question?.helpText} type="Info" noMargin closeable={false} />
      )}
      {error && (
        <div
          className={`u-margin-top--small u-margin-bottom--small ${question?.type === 'dynamic_array' ? 'u-order-1' : ''}`}>
          <FadeInMotion key={name} disabled={animationDisabled}>
            <ValidationAlert
              show={true}
              message={typeof error === 'string' ? error : error?.message}
            />
          </FadeInMotion>
        </div>
      )}
    </div>
  );
};

export default QuestionWrapper;
